/* The profile and the account settings share one look: an identity, a few labelled facts, and
   forms that read as forms. Tokens only (ADR-0007). */

/* Who this is. The mark is decoration; the name beside it is the text. */
.identity {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0 0 14px;
  padding-bottom: 14px;
  border-bottom: 1px solid var(--line);
}

.avatar {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: var(--radius-pill);
  background: var(--accent-w);
  box-shadow: inset 0 0 0 1px var(--accent-line);
  color: var(--accent);
  font-size: var(--t-head);
  font-weight: 700;
  letter-spacing: 0.02em;
}

.identityText {
  display: grid;
  gap: 1px;
  min-width: 0;
}

.identityName {
  font-size: var(--t-head);
  font-weight: 650;
  color: var(--ink);
  overflow-wrap: anywhere;
}

.identityEmail {
  font-size: var(--t-dense);
  color: var(--ink-2);
  overflow-wrap: anywhere;
}

.facts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px 16px;
  margin: 0 0 16px;
}

.facts dt {
  margin-bottom: 3px;
  font-size: var(--t-min);
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--muted);
}

.facts dd {
  margin: 0;
  font-size: var(--t-dense);
  color: var(--ink);
  overflow-wrap: anywhere;
}

.form {
  display: grid;
  gap: 12px;
}

.field {
  display: grid;
  gap: 5px;
  min-width: 0;
  font-size: var(--t-dense);
  font-weight: 600;
  color: var(--ink);
}

.field input,
.field select {
  min-height: var(--h-ctl);
  padding: 5px 9px;
  font: inherit;
  font-size: var(--t-dense);
  font-weight: 400;
  color: var(--ink);
  background: var(--panel);
  border: 1px solid var(--line-2);
  border-radius: var(--radius-ctl);
}

.field input:focus-visible,
.field select:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 1px;
}

.field input:disabled,
.field select:disabled {
  background: var(--panel-2);
  color: var(--ink-2);
}

/* A refused field is marked by the attribute the browser and a screen reader both read, not by a
   class alone — the colour is the second signal, not the only one. */
.field input[aria-invalid="true"],
.field select[aria-invalid="true"] {
  border-color: var(--crit);
}

.hint {
  font-size: var(--t-min);
  font-weight: 400;
  line-height: 1.45;
  color: var(--muted);
}

p.hint {
  margin: 0;
}

/* The theme choice: two radios, each a whole row a finger can hit. */
.choice {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  min-width: 0;
  padding: 0;
  margin: 0;
  border: 0;
}

.choiceLegend {
  width: 100%;
  padding: 0;
  margin-bottom: 4px;
  font-size: var(--t-dense);
  font-weight: 600;
  color: var(--ink);
}

.choiceNote {
  margin: 0 0 4px;
  color: var(--muted);
  font-size: var(--t-dense);
  line-height: var(--lh-13);
}

.choiceOption {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  min-height: var(--target-min);
  padding: 6px 8px;
  border-radius: var(--radius-ctl);
  cursor: pointer;
  flex: 1 1 200px;
}

.choice > p {
  width: 100%;
}

.choiceOption:hover {
  background: var(--panel-2);
}

.choiceOption input {
  margin-top: 2px;
  accent-color: var(--accent);
}

.choiceText {
  display: grid;
  gap: 1px;
  font-size: var(--t-dense);
}

.choiceName {
  font-weight: 600;
  color: var(--ink);
}

.refused {
  padding: 6px 9px;
  margin: 0;
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--crit);
  background: var(--crit-w);
  border: 1px solid var(--crit-line);
  border-left-width: 3px;
  border-radius: 6px;
}

.saved {
  margin: 0;
  font-size: var(--t-min);
  color: var(--ok);
}

/* Sessions and passkeys: one card each, the facts on the left and the one act on the right. */
.sessions {
  display: grid;
  gap: 8px;
  padding: 0;
  margin: 0;
  list-style: none;
}

.session {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  font-size: var(--t-min);
  color: var(--ink-2);
  background: var(--panel-2);
  border: 1px solid var(--line);
  border-radius: var(--radius-chip);
}

.session[data-current="yes"] {
  border-color: var(--accent-line);
  background: var(--accent-w);
}

.sessionFacts {
  display: grid;
  gap: 2px;
  min-width: 0;
}

.sessionTable {
  width: 100%;
  margin: 0;
  font-size: var(--t-min);
  color: var(--ink-2);
  border-collapse: separate;
  border-spacing: 0 8px;
}

.sessionRow td {
  padding: 10px 12px;
  background: var(--panel-2);
  border-block: 1px solid var(--line);
}

.sessionRow td:first-child {
  border-inline-start: 1px solid var(--line);
  border-radius: var(--radius-chip) 0 0 var(--radius-chip);
}

.sessionRow td:last-child {
  border-inline-end: 1px solid var(--line);
  border-radius: 0 var(--radius-chip) var(--radius-chip) 0;
}

.sessionRow[data-current="yes"] td {
  border-color: var(--accent-line);
  background: var(--accent-w);
}

.sessionAct {
  text-align: end;
}

/* The sign-in history is a list of events, not a second table: the page holds one table (the live
   sessions), and a record of what happened reads as a sequence. */
.historyList {
  display: grid;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: var(--t-min);
  color: var(--ink-2);
}

.historyRow {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 2fr) minmax(0, 2fr) minmax(0, 2fr);
  gap: 12px;
  padding: 10px 12px;
  background: var(--panel-2);
  border: 1px solid var(--line);
  border-radius: var(--radius-chip);
}

.thisOne {
  display: inline-block;
  padding: 1px 8px;
  font-size: var(--t-min);
  font-weight: 600;
  color: var(--accent);
  background: var(--panel);
  border: 1px solid var(--accent-line);
  border-radius: var(--radius-pill);
}

.thisOne::first-letter {
  text-transform: uppercase;
}

/* Settings: a short row of links to the work this account touches. */
.links {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.sub {
  margin: 14px 0 6px;
  font-size: var(--t-dense);
  font-weight: 650;
  color: var(--ink);
}
